import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import { type KeyboardEvent, useRef } from 'react'
import RememberChoiceCheckbox from './RememberChoiceCheckbox'

const t = buildTranslate('components.ai.segment_activation_modal')

interface ActivationRadioGroupProps {
  autoApprove: boolean
  rememberChoice: boolean
  onReviewOptionSelected: () => void
  onActivateOptionSelected: () => void
  /** Called for arrow-key navigation — parent decides the state change */
  onActivateOptionKeyDown: (e: KeyboardEvent) => void
  onRememberToggled: () => void
}

const ActivationRadioGroup = ({
  autoApprove,
  rememberChoice,
  onReviewOptionSelected,
  onActivateOptionSelected,
  onActivateOptionKeyDown,
  onRememberToggled,
}: ActivationRadioGroupProps) => {
  const reviewOptionRef = useRef<HTMLDivElement>(null)
  const activateOptionRef = useRef<HTMLDivElement>(null)

  /**
   * Roving-tabindex focus management (ARIA radiogroup pattern).
   * Manages DOM focus and forwards the event to the container via
   * onActivateOptionKeyDown so the parent can dispatch the Redux state change.
   */
  const handleGroupKeyDown = (e: KeyboardEvent) => {
    if (['ArrowDown', 'ArrowRight'].includes(e.key)) {
      e.preventDefault()
      activateOptionRef.current?.focus()
      onActivateOptionKeyDown(e)
    } else if (['ArrowUp', 'ArrowLeft'].includes(e.key)) {
      e.preventDefault()
      reviewOptionRef.current?.focus()
      onActivateOptionKeyDown(e)
    }
  }

  const handleReviewOptionKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onReviewOptionSelected()
    }
  }

  const handleActivateKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onActivateOptionSelected()
    }
    // Arrow keys are already handled by handleGroupKeyDown (bubble up)
  }

  return (
    <>
      <div
        className="segment-activation-modal__options"
        role="radiogroup"
        aria-label={t('activation_method_aria_label')}
        onKeyDown={handleGroupKeyDown}
      >
        <div
          ref={reviewOptionRef}
          className={`segment-activation-modal__option ${
            !autoApprove ? 'segment-activation-modal__option--selected' : ''
          }`}
          onClick={onReviewOptionSelected}
          role="radio"
          aria-checked={!autoApprove}
          tabIndex={autoApprove ? -1 : 0}
          onKeyDown={handleReviewOptionKeyDown}
        >
          <span
            className={`segment-activation-modal__radio ${
              !autoApprove ? 'segment-activation-modal__radio--selected' : ''
            }`}
          />
          <div>
            <Text.BodyText3 tag="div" bold color="grey90">
              {t('option_review_title')}
            </Text.BodyText3>
            <Text.BodyText4 tag="div" color="grey70">
              {t('option_review_subtitle')}
            </Text.BodyText4>
          </div>
        </div>

        <div
          ref={activateOptionRef}
          className={`segment-activation-modal__option ${
            autoApprove ? 'segment-activation-modal__option--selected' : ''
          }`}
          onClick={onActivateOptionSelected}
          role="radio"
          aria-checked={autoApprove}
          tabIndex={autoApprove ? 0 : -1}
          onKeyDown={handleActivateKeyDown}
        >
          <span
            className={`segment-activation-modal__radio ${
              autoApprove ? 'segment-activation-modal__radio--selected' : ''
            }`}
          />
          <div>
            <Text.BodyText3 tag="div" bold color="grey90">
              {t('option_activate_title')}
            </Text.BodyText3>
            <Text.BodyText4 tag="div" color="grey70">
              {t('option_activate_subtitle')}
            </Text.BodyText4>
            {autoApprove && (
              <div
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              >
                <RememberChoiceCheckbox
                  checked={rememberChoice}
                  onChange={onRememberToggled}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ActivationRadioGroup
